<template>
  <div class="color-select-container">

    <h3 class="product-title">
      {{ name }}
    </h3>

    <div class="main-container justify-items-center">
      <div class="imgs-container">
        <div
          class="cloudimage-360"
          id="gurkha-suv"
          :data-folder="'/assets/360_degree/'+name+'/'"
          data-filename-x="{index}.jpg"
          :data-amount-x="showed_image_amount"
        ></div>
      </div>

      <div class="selector-container">
        <h4 class="title">{{ title }}</h4>

        <div class="spectrum" :style="{ '--h': hue }">
          <div class="preview-row">
            <div class="color-preview" :style="{ backgroundColor: selectedHex }"></div>
            <div class="hex-readout">{{ selectedHex }}</div>
          </div>

          <!-- 2D Spectrum Panel: X=Hue (0-360), Y=Lightness (100-0) -->
          <div class="spectrum-panel" ref="panel" @mousedown="startDrag" @touchstart.prevent="startDrag">
            <div class="picker" :style="{ left: pickerX + 'px', top: pickerY + 'px', borderColor: selectedHex }"></div>
          </div>

          <div class="sliders">
            <label class="slider-label">Saturation</label>
            <input class="range sat" type="range" min="0" max="100" v-model.number="saturation" @input="emitSelection" />
          </div>
        </div>
      </div>
    </div>

    <div class="button-container">
      <button class="actionButton" @click="cancel">Back</button>
      <button class="actionButton" @click="next">Next</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ColorSpectrumSelector',
  props: {
    name: String,
    title: {
      type: String,
      default: 'Pick a color'
    },
    showed_image_amount: Number,
    initialColor: {
      type: String,
      default: '#E6F4EA'
    },
    // NEW: Accept pre-selected color (for restoration from saved data)
    preSelectedColor: {
      type: String,
      default: ''
    }
  },
  data() {
    // Use preSelectedColor if provided, otherwise use initialColor
    const colorToUse = this.preSelectedColor || this.initialColor;
    const { h, s, l } = parseColorToHsl(colorToUse);
    
    if (this.preSelectedColor) {
      console.log('🔄 ColorSpectrumSelector: Restoring pre-selected color:', this.preSelectedColor);
    }
    
    return {
      hue: h,
      saturation: s,
      lightness: l,
      pickerX: 0,
      pickerY: 0,
      isDragging: false
    };
  },
  computed: {
    selectedHex() {
      return hslToHex(this.hue, this.saturation, this.lightness);
    }
  },
  mounted() {
    if (window && window.CI360 && typeof window.CI360.init === 'function') {
      window.CI360.init();
    }
    // Emit initial value
    this.emitSelection();
    this.$nextTick(() => {
      this.measurePanel();
      this.updatePickerFromHsl();
      window.addEventListener('resize', this.onResize, { passive: true });
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    this.detachDrag();
  },
  methods: {
    next() {
      this.$emit('next', 'nextProduct');
    },
    cancel() {
      this.$emit('cancel', 'cancel');
    },
    emitSelection() {
      this.$emit('updateSelection', this.selectedHex);
    },
    measurePanel() {
      const el = this.$refs.panel;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      this.panelLeft = rect.left + window.scrollX;
      this.panelTop = rect.top + window.scrollY;
      this.panelWidth = rect.width;
      this.panelHeight = rect.height;
    },
    onResize() {
      this.measurePanel();
      this.updatePickerFromHsl();
    },
    updatePickerFromHsl() {
      if (!this.panelWidth || !this.panelHeight) return;
      this.pickerX = (this.hue / 360) * this.panelWidth;
      this.pickerY = ((100 - this.lightness) / 100) * this.panelHeight;
    },
    startDrag(e) {
      this.isDragging = true;
      this.attachDrag();
      this.onDrag(e);
    },
    attachDrag() {
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.endDrag);
      window.addEventListener('touchmove', this.onDrag, { passive: false });
      window.addEventListener('touchend', this.endDrag);
    },
    detachDrag() {
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.endDrag);
      window.removeEventListener('touchmove', this.onDrag);
      window.removeEventListener('touchend', this.endDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const isTouch = e.touches && e.touches.length;
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;
      const el = this.$refs.panel;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
      this.pickerX = x;
      this.pickerY = y;
      this.hue = Math.round((x / rect.width) * 360);
      this.lightness = Math.round(100 - (y / rect.height) * 100);
      this.emitSelection();
      if (isTouch) e.preventDefault();
    },
    endDrag() {
      this.isDragging = false;
      this.detachDrag();
    }
  }
};

// Utilities kept outside component to avoid re-creation per instance
function hslToHex(h, s, l) {
  const sNorm = clamp01(s / 100);
  const lNorm = clamp01(l / 100);

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const hp = (h % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (hp >= 0 && hp < 1) { r1 = c; g1 = x; b1 = 0; }
  else if (hp >= 1 && hp < 2) { r1 = x; g1 = c; b1 = 0; }
  else if (hp >= 2 && hp < 3) { r1 = 0; g1 = c; b1 = x; }
  else if (hp >= 3 && hp < 4) { r1 = 0; g1 = x; b1 = c; }
  else if (hp >= 4 && hp < 5) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }
  const m = lNorm - c / 2;
  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);
  return rgbToHex(r, g, b);
}

function rgbToHex(r, g, b) {
  const toHex = (v) => v.toString(16).padStart(2, '0');
  return `#${toHex(clamp255(r))}${toHex(clamp255(g))}${toHex(clamp255(b))}`.toUpperCase();
}

function clamp01(v) { return Math.max(0, Math.min(1, v)); }
function clamp255(v) { return Math.max(0, Math.min(255, v)); }

function parseColorToHsl(color) {
  // Supports #rgb, #rrggbb; defaults to green-like if invalid
  try {
    let hex = color.trim();
    if (hex[0] === '#') hex = hex.slice(1);
    if (hex.length === 3) {
      hex = hex.split('').map(ch => ch + ch).join('');
    }
    if (hex.length !== 6) return { h: 140, s: 60, l: 40 };
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return rgbToHsl(r, g, b);
  } catch (_) {
    return { h: 140, s: 60, l: 40 };
  }
}

function rgbToHsl(r, g, b) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      case bn: h = (rn - gn) / d + 4; break;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}
</script>

<style scoped>
.color-select-container {
  max-width: 50vw;
  margin: 0 auto;
  width: 100%;
}
.button-container {
  display: flex;
  justify-content: space-between;
  margin-top: 1vw;
}
.button-container > :first-child {
  transform: translateX(-100%);
}

.button-container > :last-child {
  transform: translateX(100%);
}
.main-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1vw;
}
.product-title {
  margin-top: 5.5vw;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  width: 24.65vw;
  height: 2.15vw;
  font-size: 2.75vw;
  font-family: BaskervilleDisplayPT, BaskervilleDisplayPT-Bold;
  font-weight: 700;
  text-align: center;
  color: #514f4c;
}
.imgs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 4.15vw;
  border-radius: 1.6667vw;
  width: 100%;
  max-width: 25vw;
  aspect-ratio: 1 / 1;
  overflow: hidden;
}
.title{
  margin-top: 4.15vw;
  height: 1.55vw;
  font-size: 2.05vw;
  font-family: Glacial Indifference, Glacial Indifference-Italic;
  font-weight: 400;
  text-align: center;
  color: #000000;
  line-height: 2.05vw;
}
.spectrum {
  margin-top: 1.5vw;
  width: 100%;
  max-width: 24vw;
}
.spectrum-panel {
  position: relative;
  width: 100%;
  height: 12vw;
  border-radius: 0.6vw;
  overflow: hidden;
  background: linear-gradient(to right,
    #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%),
    radial-gradient(circle at 50% -200%, rgba(255,255,255,0.95), rgba(255,255,255,0));
  background-blend-mode: screen;
}
.spectrum-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0) 45%, rgba(0,0,0,0) 55%, rgba(0,0,0,1));
  pointer-events: none;
}
.picker {
  position: absolute;
  width: 1.2vw;
  height: 1.2vw;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 0.2vw solid #fff;
  box-shadow: 0 0 0 0.2vw rgba(0,0,0,0.2);
  background: transparent;
}
.preview-row {
  display: flex;
  align-items: center;
  gap: 1vw;
  margin-bottom: 1vw;
}
.color-preview {
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  border: 0.2vw solid #218838;
  box-shadow: 0 0 0 0.35vw rgba(33, 136, 56, 0.45), 0 0 1vw 0 rgba(33, 136, 56, 0.35);
  box-sizing: border-box;
}
.hex-readout {
  font-size: 1.2vw;
  color: #514f4c;
  font-family: Glacial Indifference, Glacial Indifference-Bold;
}
.sliders {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.6vw;
}
.slider-label {
  font-size: 1vw;
  color: #514f4c;
}
.range {
  width: 100%;
  max-width: 24vw;
  -webkit-appearance: none;
  appearance: none;
  height: 0.6vw;
  border-radius: 0.3vw;
  background: #ddd;
  outline: none;
}
.range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1.2vw;
  height: 1.2vw;
  border-radius: 50%;
  background: #514f4c;
  cursor: pointer;
}
.range::-moz-range-thumb {
  width: 1.2vw;
  height: 1.2vw;
  border: none;
  border-radius: 50%;
  background: #514f4c;
  cursor: pointer;
}
.range.hue {
  background: linear-gradient(to right,
    #ff0000 0%, #ff00ff 17%, #0000ff 33%, #00ffff 50%, #00ff00 67%, #ffff00 83%, #ff0000 100%);
}
.range.sat {
  background: linear-gradient(to right, hsl(var(--h, 140), 0%, 50%), hsl(var(--h, 140), 100%, 50%));
}
.range.light {
  background: linear-gradient(to right, hsl(var(--h, 140), 100%, 0%), hsl(var(--h, 140), 100%, 50%), hsl(var(--h, 140), 100%, 100%));
}

.actionButton {
  width: 8vw;
  height: 2.05vw;
  border-radius: 1.025vw;
  background: #cfc5b3;
  color: black;
  border: none;
  font-size: 1.4vw;
  font-family: Glacial Indifference, Glacial Indifference-Bold;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  margin-top: 1vw;
}

@media screen and (max-width: 768px){
  .main-container {
    grid-template-columns: 1fr;
    place-items: center;
  }
  .selector-container {
    width: 85%;
    margin: 0 auto;
  }
  .color-select-container{
    max-width: 100vw;
  }
  .product-title {
    margin-top: 10.4167vw;
    height: 9.1146vw;
    width: 100%;
    font-size: 7.1615vw;
    font-family: BaskervilleDisplayPT, BaskervilleDisplayPT-Bold;
    font-weight: 700;
    text-align: center;
    color: #514f4c;
  }
  .imgs-container {
    margin-top: 4.9479vw;
    width: 100%;
    max-width: 67.7083vw;
    aspect-ratio: 1 / 1;
  }
  .title{
    margin-top: 5.2083vw;
    font-size: 4.9479vw;
    line-height: 5.599vw;
    height: 5.4688vw;
  }
  .spectrum { width: 100%; max-width: none; }
  .spectrum-panel { height: 32vw; border-radius: 1.6vw; }
  .picker { width: 4vw; height: 4vw; border-width: 0.6vw; box-shadow: 0 0 0 0.6vw rgba(0,0,0,0.15); }
  .color-preview {
    width: 9.7656vw;
    height: 9.7656vw;
    border-width: 0.4vw;
    box-shadow: 0 0 0 1vw rgba(33, 136, 56, 0.5), 0 0 2.5vw 0 rgba(33, 136, 56, 0.3);
  }
  .hex-readout { font-size: 4vw; }
  .slider-label { font-size: 3.2vw; }
  .range { width: 100%; max-width: none; height: 1.6vw; border-radius: 0.8vw; }
  .range::-webkit-slider-thumb { width: 4vw; height: 4vw; }
  .range::-moz-range-thumb { width: 4vw; height: 4vw; }
  .button-container { margin-top: 18.2292vw; }
  .button-container > :first-child { transform: translateX(100%); }
  .button-container > :last-child { transform: translateX(-100%); }
  .actionButton {
    width: 19.0104vw;
    height: 4.9479vw;
    border-radius: 2.474vw;
    font-size: 3.2552vw;
  }
}
</style>

